import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Plus, Edit2, Trash2, Search, HelpCircle, RefreshCw, Building2, Users, MapPin, ChevronDown, ChevronUp, User } from 'lucide-react';
import { format, parseISO, differenceInDays, startOfMonth, endOfMonth } from 'date-fns';
import { dateLocales } from '../i18n';
import PropertyForm from '../components/PropertyForm';
import PropertyFilters from '../components/PropertyFilters';
import Pagination from '../components/Pagination';
import PropertyStats from '../components/PropertyStats';
import LoadingSpinner from '../components/LoadingSpinner';
import Tooltip from '../components/Tooltip';
import ConfirmDialog from '../components/ConfirmDialog';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';
import { logActivity } from '../utils/activityLogger';
import { useNotification } from '../contexts/NotificationContext';

interface Property {
  id: string;
  name: string;
  address: string;
  capacity: number;
  owner: {
    first_name: string;
    last_name: string;
  };
}

interface BookingStat {
  month: string;
  bookings: number;
  occupancyRate?: number;
  averageNights?: number;
}

interface PropertyStats {
  bookingStats: BookingStat[];
  totalBookings: number;
  averageNights: number;
  occupancyRate: number;
}

const ITEMS_PER_PAGE = 10;

export default function Properties() {
  const { t, i18n } = useTranslation();
  const { userRole } = useAuth();
  const { addNotification } = useNotification();
  const [properties, setProperties] = useState<Property[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedProperty, setExpandedProperty] = useState<string | null>(null);
  const [propertyStats, setPropertyStats] = useState<{ [key: string]: PropertyStats }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const canManageProperties = ['owner', 'admin', 'super_admin'].includes(userRole || '');

  useKeyboardShortcut('n', () => {
    if (canManageProperties) {
      setSelectedProperty(null);
      setShowForm(true);
    }
  }, true);

  const fetchProperties = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setIsRefreshing(true);
      
      const countResult = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .ilike('name', `%${searchTerm}%`);

      setTotalCount(countResult.count || 0);

      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const { data, error } = await supabase
        .from('properties')
        .select(`
          id,
          name,
          address,
          capacity,
          owner:profiles(first_name, last_name)
        `)
        .ilike('name', `%${searchTerm}%`)
        .range(start, start + ITEMS_PER_PAGE - 1)
        .order('name');

      if (error) throw error;
      setProperties(data || []);
    } catch (error: any) {
      console.error('Error fetching properties:', error);
      setError(error.message);
      addNotification('error', t('properties.notifications.error'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [addNotification, searchTerm, currentPage, t]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  async function fetchPropertyStats(propertyId: string) {
    try {
      const startDate = startOfMonth(new Date());
      startDate.setMonth(startDate.getMonth() - 11); // Last 12 months
      
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('check_in_date, check_out_date')
        .eq('property_id', propertyId)
        .gte('check_in_date', startDate.toISOString());

      if (error) throw error;

      const monthlyStats: { [key: string]: BookingStat } = {};
      for (let i = 0; i < 12; i++) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const month = format(date, 'MMMM', { locale: dateLocales[i18n.language] });
        monthlyStats[month] = { month, bookings: 0 };
      }

      let totalNights = 0;
      const occupiedDays = new Set<string>();

      bookings?.forEach(booking => {
        const month = format(parseISO(booking.check_in_date), 'MMMM', { locale: dateLocales[i18n.language] });
        const nights = differenceInDays(parseISO(booking.check_out_date), parseISO(booking.check_in_date));
        totalNights += nights;

        let currentDate = parseISO(booking.check_in_date);
        while (currentDate < parseISO(booking.check_out_date)) {
          occupiedDays.add(format(currentDate, 'yyyy-MM-dd'));
          currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
        }

        if (monthlyStats[month]) {
          monthlyStats[month].bookings++;
        }
      });

      const totalDays = differenceInDays(new Date(), startDate);
      const occupancyRate = (occupiedDays.size / totalDays) * 100;

      const stats = Object.values(monthlyStats).reverse();
      const totalBookings = bookings?.length || 0;
      const averageNights = totalBookings > 0 ? totalNights / totalBookings : 0;

      setPropertyStats(prev => ({
        ...prev,
        [propertyId]: {
          bookingStats: stats,
          totalBookings,
          averageNights,
          occupancyRate
        }
      }));
    } catch (error) {
      console.error('Error fetching property stats:', error);
      addNotification('error', t('properties.notifications.error'));
      
      setPropertyStats(prev => ({
        ...prev,
        [propertyId]: {
          bookingStats: [],
          totalBookings: 0,
          averageNights: 0,
          occupancyRate: 0
        }
      }));
    }
  }

  const handleExpandProperty = async (propertyId: string) => {
    if (expandedProperty === propertyId) {
      setExpandedProperty(null);
    } else {
      setExpandedProperty(propertyId);
      if (!propertyStats[propertyId]) {
        await fetchPropertyStats(propertyId);
      }
    }
  };

  const handleDelete = async (id: string) => {
    setPropertyToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!propertyToDelete) return;

    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyToDelete);

      if (error) throw error;

      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (userId) {
        await logActivity({
          user_id: userId,
          action: 'delete',
          entity_type: 'property',
          entity_id: propertyToDelete
        });
      }

      addNotification('success', t('properties.notifications.deleted'));
      fetchProperties();
    } catch (error: any) {
      console.error('Error deleting property:', error);
      addNotification('error', t('properties.notifications.error'));
    } finally {
      setShowDeleteConfirm(false);
      setPropertyToDelete(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 text-center">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg inline-block">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={fetchProperties}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          {t('properties.title')}
          <Tooltip content={t('properties.tooltip')}>
            <HelpCircle className="w-5 h-5 text-gray-400" />
          </Tooltip>
        </h1>
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <button
            onClick={fetchProperties}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            disabled={isRefreshing}
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
          {canManageProperties && (
            <button
              onClick={() => {
                setSelectedProperty(null);
                setShowForm(true);
              }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 w-full sm:w-auto justify-center"
            >
              <Plus size={20} />
              <span>{t('properties.new')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <PropertyFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
      </div>

      {/* Mobile View */}
      <div className="block lg:hidden space-y-4">
        {properties.map((property) => (
          <div key={property.id} className="bg-white rounded-lg shadow">
            <div className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{property.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{property.address}</p>
                </div>
                {canManageProperties && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedProperty(property);
                        setShowForm(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(property.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users size={16} className="text-gray-400" />
                  <span>{property.capacity} {t('common.people')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User size={16} className="text-gray-400" />
                  <span>{property.owner.first_name} {property.owner.last_name}</span>
                </div>
              </div>

              <button
                onClick={() => handleExpandProperty(property.id)}
                className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 py-2 border-t"
              >
                {expandedProperty === property.id ? (
                  <>
                    <ChevronUp size={16} />
                    {t('common.less')}
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} />
                    {t('common.more')}
                  </>
                )}
              </button>
            </div>

            {expandedProperty === property.id && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <PropertyStats
                  propertyId={property.id}
                  bookingStats={propertyStats[property.id]?.bookingStats || []}
                  totalBookings={propertyStats[property.id]?.totalBookings}
                  averageNights={propertyStats[property.id]?.averageNights}
                  occupancyRate={propertyStats[property.id]?.occupancyRate}
                />
              </div>
            )}
          </div>
        ))}
        {properties.length === 0 && (
          <div className="text-center py-8 text-gray-500 bg-white rounded-lg shadow">
            {t('common.noData')}
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('properties.form.name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('properties.form.address')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('properties.form.capacity')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('properties.form.owner')}
              </th>
              {canManageProperties && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {properties.map((property) => (
              <React.Fragment key={property.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleExpandProperty(property.id)}
                        className="mr-2 text-gray-400 hover:text-gray-600"
                      >
                        {expandedProperty === property.id ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </button>
                      <span className="text-sm font-medium text-gray-900">
                        {property.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {property.address}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {property.capacity} {t('common.people')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {property.owner.first_name} {property.owner.last_name}
                  </td>
                  {canManageProperties && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedProperty(property);
                          setShowForm(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                        title={t('common.edit')}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(property.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('common.delete')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
                {expandedProperty === property.id && (
                  <tr>
                    <td colSpan={canManageProperties ? 5 : 4} className="px-6 py-4 bg-gray-50">
                      <PropertyStats
                        propertyId={property.id}
                        bookingStats={propertyStats[property.id]?.bookingStats || []}
                        totalBookings={propertyStats[property.id]?.totalBookings}
                        averageNights={propertyStats[property.id]?.averageNights}
                        occupancyRate={propertyStats[property.id]?.occupancyRate}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {properties.length === 0 && (
              <tr>
                <td
                  colSpan={canManageProperties ? 5 : 4}
                  className="px-6 py-4 text-center text-gray-500"
                >
                  {t('common.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {showForm && (
        <PropertyForm
          property={selectedProperty}
          onClose={() => {
            setShowForm(false);
            setSelectedProperty(null);
          }}
          onSuccess={fetchProperties}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('properties.delete')}
        message={t('properties.deleteConfirm')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}